import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DialogHeader from "../../../components/Dialog/DialogHeader";
import UserDialog from "../../../components/Dialog/UserDialog";
import { Api } from "../../../request/api/api";
import { FighterSkillsInfo } from "../../../model/Dto/Skill";

interface State {
  branchId: string | undefined;
  skillId: string | undefined;
  isBuiltIn: boolean;
  experienceToSpend: number;
}

export interface AddSkillDialogProps {
  fighterId: string;
  open: boolean;
  fetchData: () => void;
  onClose: () => void;
}

export default function AddSkillDialog({
  fighterId,
  onClose,
  fetchData,
  open,
}: AddSkillDialogProps) {
  const [skillInfo, setSkillInfo] = useState<State>({
    branchId: undefined,
    skillId: undefined,
    isBuiltIn: false,
    experienceToSpend: 0,
  });

  const [inputError, setInputError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [skillsInfo, setSkillsInfo] = useState<FighterSkillsInfo | null>(null);
  const [experienceError, setExperienceError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setSkillInfo({
        branchId: undefined,
        skillId: undefined,
        isBuiltIn: false,
        experienceToSpend: 0,
      });
      setExperienceError(null);
      setSuccess(false);

      Api.fighter.getFighterSkills(fighterId)
        .then((result: FighterSkillsInfo) => {
          setSkillsInfo(result);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [open, fighterId]);

  useEffect(() => {
    if (skillsInfo && skillInfo.experienceToSpend > skillsInfo.fighterExp) {
      setExperienceError(`У бойца недостаточно опыта. Доступно: ${skillsInfo.fighterExp}`);
    } else {
      setExperienceError(null);
    }
  }, [skillInfo.experienceToSpend, skillsInfo]);

  const selectedBranch = skillsInfo?.skillBranches.find(b => b.id === skillInfo.branchId) ?? null;

  const handleBranchChange = (event: SelectChangeEvent) => {
    const branchId = event.target.value;
    setSkillInfo(prev => ({ ...prev, branchId, skillId: undefined }));
    setInputError(false);
  };

  const handleSkillChange = (event: SelectChangeEvent) => {
    setSkillInfo(prev => ({ ...prev, skillId: event.target.value }));
    setInputError(false);
  };

  const handleBuiltInChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSkillInfo(prev => ({ ...prev, isBuiltIn: event.target.checked }));
  };

  const handleExperienceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 0;
    setSkillInfo(prev => ({ ...prev, experienceToSpend: value }));
  };

  const validateForm = () => {
    if (!skillInfo.skillId) {
      setInputError(true);
      return false;
    }
    if (skillInfo.experienceToSpend < 0) {
      setExperienceError("Количество опыта не может быть отрицательным");
      return false;
    }
    if (skillsInfo && skillInfo.experienceToSpend > skillsInfo.fighterExp) {
      setExperienceError(`У бойца недостаточно опыта. Доступно: ${skillsInfo.fighterExp}`);
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (validateForm()) {
      setLoading(true);
      try {
        const result = await Api.fighter.addSkill(
          fighterId,
          skillInfo.skillId!,
          skillInfo.isBuiltIn,
          skillInfo.experienceToSpend
        );
        setLoading(false);
        if (result) {
          setSuccess(true);
          setTimeout(() => {
            handleClose();
            fetchData();
          }, 1000);
        } else {
          setInputError(true);
        }
      } catch (error) {
        setLoading(false);
        setInputError(true);
      }
    }
  };

  const resetForm = () => {
    setSkillInfo({
      branchId: undefined,
      skillId: undefined,
      isBuiltIn: false,
      experienceToSpend: 0,
    });
    setInputError(false);
    setExperienceError(null);
    setSuccess(false);
    setSkillsInfo(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <UserDialog handleClose={handleClose} open={open}>
      <DialogHeader
        loading={loading}
        title="Добавить навык бойцу"
      />
      <DialogContent sx={{ minHeight: "300px", minWidth: {xs: "300px", lg: "450px"} }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {success && (
            <Alert severity="success">
              Навык успешно добавлен!
            </Alert>
          )}

          {inputError && (
            <Alert severity="error" onClose={() => setInputError(false)}>
              Ошибка при добавлении навыка
            </Alert>
          )}

          {skillsInfo && (
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Доступный опыт бойца:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {skillsInfo.fighterExp}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          <TextField
            label="Стоимость в очках опыта (XP)"
            type="number"
            value={skillInfo.experienceToSpend}
            onChange={handleExperienceChange}
            error={!!experienceError}
            helperText={experienceError || "Введите количество XP, которые будут потрачены"}
            variant="outlined"
            fullWidth
            InputProps={{
              inputProps: {
                min: 0,
                max: skillsInfo?.fighterExp || 0
              }
            }}
            disabled={loading || !skillsInfo}
          />

          {loading ? (
            <Typography>Загрузка навыков...</Typography>
          ) : skillsInfo && skillsInfo.skillBranches.length === 0 ? (
            <Typography>Нет доступных навыков</Typography>
          ) : skillsInfo ? (
            <>
              <FormControl variant="filled" fullWidth disabled={loading}>
                <InputLabel id="branch-select-label">Выберите ветку навыков</InputLabel>
                <Select
                  labelId="branch-select-label"
                  value={skillInfo.branchId || ''}
                  onChange={handleBranchChange}
                  label="Выберите ветку навыков"
                >
                  {skillsInfo.skillBranches.map(branch => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedBranch && (
                <FormControl
                  variant="filled"
                  fullWidth
                  error={inputError && !skillInfo.skillId}
                  disabled={loading}
                >
                  <InputLabel id="skill-select-label">Выберите навык</InputLabel>
                  <Select
                    labelId="skill-select-label"
                    value={skillInfo.skillId || ''}
                    onChange={handleSkillChange}
                    label="Выберите навык"
                  >
                    {selectedBranch.skills.map(skill => (
                      <MenuItem
                        key={skill.id}
                        value={skill.id}
                        sx={{ opacity: skill.alreadyPurchased ? 0.6 : 1 }}
                        disabled={skill.alreadyPurchased}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Typography>
                            {skill.name}
                            {skill.alreadyPurchased && " (уже изучен)"}
                          </Typography>
                          {skill.alreadyPurchased && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ✓
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          ) : null}

          {skillsInfo && skillInfo.skillId && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={skillInfo.isBuiltIn}
                  onChange={handleBuiltInChange}
                  name="builtIn"
                  color="primary"
                  disabled={loading}
                />
              }
              label="Навык/психосила не увеличивает стоимость бойца"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Отмена
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          color="primary"
        >
          {loading ? "Добавление..." : "Добавить навык"}
        </Button>
      </DialogActions>
    </UserDialog>
  );
}
